import { notification } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { orpcClient } from "../../lib/orpc-client";
import { single } from "../../lib/orpc-batch";
import AddItemDialog from "./AddItemDialog";
import CategorySidebar from "./CategorySidebar";
import {
  allSteelSubCategories,
  screwSubCategories,
  type Item,
  type SubTypeOption,
} from "./data";
import {
  mapServerItem,
  REAL_CATEGORY_IDS,
  toServerMainCategory,
  toServerSubCategory,
  type ServerItemRow,
} from "./itemMapping";
import ItemEditPanel from "./ItemEditPanel";
import ItemGrid from "./ItemGrid";

const SUBTYPE_LABELS = new Map(
  [...allSteelSubCategories, ...screwSubCategories].map((sub) => [
    sub.id,
    sub.name,
  ]),
);

const SUBTYPE_STATIC_ORDER: Record<string, string[]> = {
  steel: allSteelSubCategories.map((sub) => sub.id),
  screw: screwSubCategories.map((sub) => sub.id),
};

function buildSubTypes(
  categoryId: string,
  counts: Map<string, number>,
): SubTypeOption[] {
  const ordered: SubTypeOption[] = [];
  const seen = new Set<string>();
  for (const id of SUBTYPE_STATIC_ORDER[categoryId] ?? []) {
    const count = counts.get(id);
    if (count) {
      ordered.push({ id, name: SUBTYPE_LABELS.get(id) ?? id, count });
      seen.add(id);
    }
  }
  for (const [id, count] of counts) {
    if (!seen.has(id)) {
      ordered.push({ id, name: SUBTYPE_LABELS.get(id) ?? id, count });
    }
  }
  return ordered;
}

function toItemFields(item: Item) {
  return {
    mainCategory: toServerMainCategory(item.categoryId),
    subCategory: toServerSubCategory(item.categoryId, item.steelType),
    sectionDimension: item.spec ?? null,
    name: item.name,
    description: item.description ?? null,
  };
}

function ItemEditorPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedSubType, setSelectedSubType] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [api, contextHolder] = notification.useNotification();

  const loadItems = async () => {
    setLoading(true);
    try {
      const [companyItems, defaultItems] = await Promise.all([
        orpcClient.item.list({}),
        orpcClient.defaultItem.list({}),
      ]);
      setItems([
        ...companyItems.map((row: ServerItemRow) => mapServerItem(row, false)),
        ...defaultItems.map((row: ServerItemRow) => mapServerItem(row, true)),
      ]);
    } catch (error) {
      api.error({ message: t("itemEditor.notify.loadFailed"), description: String(error) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const filteredItems = useMemo(() => {
    let result = items;
    if (selectedCategory === "uncategorized") {
      result = result.filter((item) => !REAL_CATEGORY_IDS.has(item.categoryId));
    } else if (selectedCategory !== "all") {
      result = result.filter((item) => item.categoryId === selectedCategory);
    }
    if (
      (selectedCategory === "steel" || selectedCategory === "screw") &&
      selectedSubType
    ) {
      result = result.filter((item) => item.steelType === selectedSubType);
    }
    return result;
  }, [items, selectedCategory, selectedSubType]);

  const itemCounts = useMemo(() => {
    const counts: Record<string, number> = { all: items.length, uncategorized: 0 };
    for (const item of items) {
      counts[item.categoryId] = (counts[item.categoryId] ?? 0) + 1;
      if (!REAL_CATEGORY_IDS.has(item.categoryId)) {
        counts.uncategorized += 1;
      }
    }
    return counts;
  }, [items]);

  const subTypesByCategory = useMemo(() => {
    const byCategory: Record<string, Map<string, number>> = {};
    for (const item of items) {
      if (
        (item.categoryId === "steel" || item.categoryId === "screw") &&
        item.steelType
      ) {
        const counts = (byCategory[item.categoryId] ??= new Map());
        counts.set(item.steelType, (counts.get(item.steelType) ?? 0) + 1);
      }
    }
    const result: Record<string, SubTypeOption[]> = {};
    for (const [categoryId, counts] of Object.entries(byCategory)) {
      result[categoryId] = buildSubTypes(categoryId, counts);
    }
    return result;
  }, [items]);

  const handleAddNewItem = async (newItem: Item) => {
    try {
      const created = await single(
        orpcClient.item.create([toItemFields(newItem)])
      );
      api.success({ message: t("itemEditor.notify.created") });
      await loadItems();
      if (created) setSelectedItem(mapServerItem(created as ServerItemRow, false));
    } catch (error) {
      api.error({ message: t("itemEditor.notify.createFailed"), description: String(error) });
    }
  };

  const handleSaveItem = async (item: Item) => {
    try {
      const updated = await single(
        orpcClient.item.update([
          {
            id: item.id,
            ...toItemFields(item),
          },
        ])
      );
      api.success({ message: t("itemEditor.notify.saved") });
      await loadItems();
      if (updated) setSelectedItem(mapServerItem(updated as ServerItemRow, false));
    } catch (error) {
      api.error({ message: t("itemEditor.notify.saveFailed"), description: String(error) });
    }
  };

  const handleCopyItem = async (item: Item) => {
    try {
      const created = await single(
        orpcClient.item.create([
          {
            ...toItemFields(item),
            name: `${item.name}-COPY-${String(Date.now()).slice(-4)}`,
          },
        ])
      );
      api.success({ message: t("itemEditor.notify.copied") });
      await loadItems();
      if (created) setSelectedItem(mapServerItem(created as ServerItemRow, false));
    } catch (error) {
      api.error({ message: t("itemEditor.notify.copyFailed"), description: String(error) });
    }
  };

  return (
    <div className="flex h-[calc(100vh-140px)] flex-col overflow-hidden bg-gray-50">
      {contextHolder}
      <div className="flex flex-1 overflow-hidden">
        <CategorySidebar
          selectedCategory={selectedCategory}
          selectedSubType={selectedSubType}
          counts={itemCounts}
          subTypes={subTypesByCategory}
          onSelectCategory={setSelectedCategory}
          onSelectSubType={setSelectedSubType}
        />
        <ItemGrid
          items={filteredItems}
          selectedItem={selectedItem}
          loading={loading}
          onSelectItem={setSelectedItem}
          onAddItem={() => setIsAddDialogOpen(true)}
        />
        <ItemEditPanel
          item={selectedItem}
          onSave={handleSaveItem}
          onCancel={() => setSelectedItem(null)}
          onCopy={handleCopyItem}
        />
      </div>

      <AddItemDialog
        open={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        onAdd={handleAddNewItem}
      />
    </div>
  );
}

export default ItemEditorPage;
